<!-- Initial HTTP fetch — status not yet known -->
@if(loading && !summaryStatus){
<div class="flex flex-col h-full w-full items-center justify-center gap-4 p-6">
    <!-- Loading spinner Lottie animation while fetching initial status -->
    <app-lottie-animation [animationData]="queueAnimationData" [loop]="true" [autoplay]="true" width="80px"
        height="80px">
    </app-lottie-animation>
    <p class="text-sm text-gray-500 font-bricolage">Loading summary…</p>
</div>
}@else if (openHistory){
<div class="flex flex-col h-full p-3 w-full">
    <div class="flex items-center gap-2 border-gray-200">
        <app-button [icon]="SVG.GO_BACK" [iconSource]="'svg'" [size]="'default'" variant="text"
            (clicked)="openHistory = false">
        </app-button>
        <p class="font-semibold text-lg">Summary</p>
    </div>
    @if(loadingHistory){
    <div class="flex justify-center items-center flex-1 p-6">
        <div class="loader"></div>
    </div>
    } @else if (summaryHistoryList.length === 0) {
    <div class="flex flex-col items-center justify-center flex-1 p-6 text-gray-500">
        <span>No summary history yet.</span>
    </div>
    } @else {
    <div class="flex flex-col p-4 overflow-auto">
        @for (item of summaryHistoryList; track item._id ?? $index) {
        <div class="flex flex-row items-start justify-between gap-3 py-3 border-b last:border-b-0 rounded-lg px-3 transition-colors"
            [class.border-orange-200]="item._id === latestSummaryDetails._id"
            [class.border-gray-200]="item._id !== latestSummaryDetails._id"
            [class.bg-orange-50]="item._id === latestSummaryDetails._id">
            <div class="flex flex-col gap-1 min-w-0 flex-1">
                <span class="text-sm font-medium text-gray-800">{{ item.name || (item.createdAt ? (item.createdAt |
                    date:'d MMMM yyyy') : 'Summary') }}</span>
                <span class="text-xs text-gray-500">{{ item.createdAt | date:'d MMMM yyyy, h:mm a' }}</span>
                <app-user-name-display [user]="item.createdBy" [variant]="'avatar-name'"></app-user-name-display>
            </div>
            <div class="flex gap-2 items-center justify-between">
                @if (item._id === latestSummaryDetails._id) {
                <span
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-orange-500 text-white leading-none">
                    Current Version
                </span>
                }
                <app-menu #summaryItemMenu [items]="getSummaryMenuItems(item)" class="flex-shrink-0">
                    <div class="p-1.5 rounded-base hover:bg-gray-100 transition-colors cursor-pointer text-gray-500 flex items-center justify-center"
                        (click)="$event.stopPropagation(); summaryItemMenu.toggle($event)">
                        <i class="pi pi-ellipsis-v text-base"></i>
                    </div>
                </app-menu>
            </div>
        </div>
        }
    </div>
    }
</div>

<!-- Completed summary available (explicit COMPLETED status, or a legacy record with summary text
     that predates the status field) — always wins once we actually have content to show. -->
}@else if(hasCompletedSummary){
<div class="flex flex-col h-full w-full min-h-0">
    <div class="flex flex-shrink-0 justify-between items-center p-6 w-full">
        <p class="flex items-center font-semibold text-lg gap-2">
            <span>Summary</span>
            <app-icon [type]="GEOTECHNICAL_ANALYSIS_SVG.AI_GENERATED" [source]="'svg'" [size]="'default'" />
        </p>
        <div class="flex gap-3">
            @if (!isViewer) {
            <app-button [icon]="GEOTECHNICAL_ANALYSIS_SVG.AI_GENERATED" [iconSource]="'svg'" [size]="'default'"
                (clicked)="onRegenerate()" variant="outlined" severity="contrast" pTooltip="Re-Generate"
                tooltipPosition="top">
            </app-button>

            <app-button [icon]="SVG.DOWNLOAD" [iconSource]="'svg'" [size]="'default'" variant="outlined"
                pTooltip="Download" tooltipPosition="top" (clicked)="onDownloadSummaryItem(latestSummaryDetails._id)"
                [disabled]="downloadLoading()">
            </app-button>
            }

            <app-button [icon]="SVG.VERSION" [iconSource]="'svg'" [size]="'default'" variant="outlined"
                pTooltip="History" (clicked)="onHistoryClick()" tooltipPosition="top">
            </app-button>
        </div>
    </div>
    <div class="flex-1 min-h-0 max-h-[calc(100vh-14rem)] overflow-y-auto overflow-x-hidden scrollbar-thin">
        <div class="prose prose-compact text-justify text-sm px-6 pb-6 highlight-transition rounded-base prose-ul:pl-1 prose-ol:pl-2 2xl:prose-ul:pl-4 2xl:prose-ol:pl-3 sm:prose-ul:pl-3"
            [innerHTML]="latestSummaryDetails.summary | formatText" dynamicComponentRenderer
            style="line-height: 1.5rem;">
        </div>
    </div>
</div>

<!-- Credits exhausted (backend told us directly, or we're in a non-terminal state with no
     completed summary yet while the org's agent credits are already known to be at zero) -->
}@else if(creditsExhausted){
<div class="flex items-center justify-center h-full">
    <app-credits-overview variant="exhaustionlayout"  feature="GEOTECHNICAL_SUMMARY" [featureId]="analysisId"
        [forceExhausted]="creditsExhausted" (resume)="fetchLatestSummary()" />
</div>

<!-- Generation in progress (IN_PROGRESS status from backend / socket) -->
}@else if(summaryStatus === GEOTECHNICAL_SUMMARY_STATUS.IN_PROGRESS){
<div class="flex flex-col h-full">
    <div class="flex justify-end px-4 pt-3">
        <app-button [icon]="SVG.VERSION" [iconSource]="'svg'" [size]="'default'" variant="outlined" pTooltip="History"
            (clicked)="onHistoryClick()" tooltipPosition="top">
        </app-button>
    </div>
    <app-generation-loader title="Generating Report summary" class="h-full"
        description="Analyzing documents and preparing key highlights for you."
        [animationData]="generationAnimationData" [bottomImageSvg]="GEOTECHNICAL_ANALYSIS_SVG.SUMMARIZE"
        feature="GEOTECHNICAL_SUMMARY" [featureId]="analysisId" (resume)="fetchLatestSummary()" />
</div>

<!-- Generation failed -->
}@else if(summaryStatus === GEOTECHNICAL_SUMMARY_STATUS.FAILED){
<app-generation-loader title="Failed to generate Report Summary"
    description="We couldn't process the reports right now. Please retry." actionLabel="Retry"
    [bottomImageSvg]="GEOTECHNICAL_ANALYSIS_SVG.SUMMARIZE" (action)="onSubmitSummaryDescription()" />

<!-- Not yet generated / queued (NOT_STARTED, or unset/null — safe final fallback, never blank) -->
}@else{
<app-generation-loader title="Report summary is in queue"
    description="Your reports are waiting to be processed and summarized shortly." [animationData]="queueAnimationData"
    animationWidth="80px" animationHeight="80px" [bottomImageSvg]="GEOTECHNICAL_ANALYSIS_SVG.SUMMARIZE"  />
}

<app-dialog [visible]="summaryDescriptionDialogVisible" (closed)="summaryDescriptionDialogVisible = false"
    title="Re-Generate Summary" class="max-w-[40rem]">
    <ng-template #bodyTemplate>
        <div class="flex flex-col gap-3">
            <p class="text-xs text-gray-400">
                Optionally provide instructions to customise the summary — focus areas, key findings to highlight,
                preferred structure, etc. Leave blank to regenerate with default settings.
            </p>

            <!-- Workspace-level instructions badge — shown when textarea is pre-filled from workspace config -->
            @if (instructionsFromWorkspace) {
            <div
                class="flex items-center gap-1.5 self-start bg-blue-50 border border-blue-200 text-blue-600 text-xs font-medium px-2.5 py-1 rounded-full">
                <i class="pi pi-building text-[10px]"></i>
                <span>Workspace default instructions</span>
            </div>
            }

            <textarea placeholder="e.g. Focus on soil bearing capacity, highlight risks and recommendations…" rows="5"
                class="w-full p-3 border border-gray-300 rounded-base text-sm outline-none placeholder:italic placeholder:text-gray-400 focus:border-orange-500 resize-none"
                [(ngModel)]="summaryDescriptionInput" (input)="instructionsFromWorkspace = false"></textarea>
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end">
            <app-button label="Submit" severity="primary" variant="filled" (clicked)="onSubmitSummaryDescription()" />
        </div>
    </ng-template>
</app-dialog>

<!-- ── View summary dialog ───────────────────────────────────────────────── -->
<app-dialog [visible]="viewDialogVisible" (closed)="viewDialogVisible = false" [title]="viewSummaryTitle"
    class="max-w-[60rem] w-[60rem]">
    <ng-template #bodyTemplate>
        @if (viewSummaryLoading) {
        <div class="flex justify-center items-center h-64">
            <div class="loader"></div>
        </div>
        } @else {
        <div class="prose prose-compact text-justify text-sm max-h-[70vh] overflow-y-auto highlight-transition rounded-base prose-ul:pl-1 prose-ol:pl-2 2xl:prose-ul:pl-4 2xl:prose-ol:pl-3 sm:prose-ul:pl-3"
            [innerHTML]="viewSummaryContent | formatText" dynamicComponentRenderer style="line-height: 1.5rem;">
        </div>
        }
    </ng-template>
</app-dialog>

<!-- ── Rename summary dialog ─────────────────────────────────────────────── -->
<app-dialog [visible]="renameDialogVisible" (closed)="renameDialogVisible = false" title="Rename Summary"
    class="max-w-[30rem]">
    <ng-template #bodyTemplate>
        <input type="text" placeholder="Summary name"
            class="w-full p-3 border border-gray-300 rounded-base text-sm outline-none placeholder:text-gray-400 focus:border-orange-500"
            [(ngModel)]="renameInput" (keydown.enter)="onSubmitRename()" />
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end">
            <app-button label="Save" severity="primary" variant="filled" (clicked)="onSubmitRename()" />
        </div>
    </ng-template>
</app-dialog>
